import { useAuth } from "@/auth/AuthProvider";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ActivationPackService } from "@/services/database/ActivationPackService";
import {
  CreateActivationPackParams,
  UpdateActivationPackParams,
} from "@/types/activation/activation-pack";

// =====================
// Pack Queries
// =====================

export const useActivationPacks = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["activation-packs", user?.id],
    queryFn: () => ActivationPackService.listPacks(user?.id),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!user?.id,
  });
};

export const useActivationPack = (packId: string) => {
  return useQuery({
    queryKey: ["activation-pack", packId],
    queryFn: () => ActivationPackService.getPackWithTarget(packId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!packId,
  });
};

export const useActivationPacksForAgent = (agentId: string) => {
  return useQuery({
    queryKey: ["activation-packs-agent", agentId],
    queryFn: () => ActivationPackService.listPacksForAgent(agentId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!agentId,
  });
};

export const useActivationPacksForPortal = (portalId: string) => {
  return useQuery({
    queryKey: ["activation-packs-portal", portalId],
    queryFn: () => ActivationPackService.listPacksForPortal(portalId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!portalId,
  });
};

// =====================
// Pack Mutations
// =====================

export const useCreateActivationPack = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: CreateActivationPackParams) =>
      ActivationPackService.createPack(params),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["activation-packs"] });
      if (data?.agent_id) {
        queryClient.invalidateQueries({
          queryKey: ["activation-packs-agent", data.agent_id],
        });
      }
      if (data?.portal_id) {
        queryClient.invalidateQueries({
          queryKey: ["activation-packs-portal", data.portal_id],
        });
      }
      toast.success("Activation pack created");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create activation pack");
    },
  });
};

export const useUpdateActivationPack = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      packId,
      updates,
    }: {
      packId: string;
      updates: UpdateActivationPackParams;
    }) => ActivationPackService.updatePack(packId, updates),
    onSuccess: (_, { packId }) => {
      queryClient.invalidateQueries({ queryKey: ["activation-packs"] });
      queryClient.invalidateQueries({ queryKey: ["activation-pack", packId] });
      // Artwork-affecting pack edits retire the current printer draft on the
      // server. Refetch here so the UI never keeps showing (or sending) URLs
      // from the superseded artwork snapshot.
      queryClient.invalidateQueries({ queryKey: ["print-order", packId] });
      toast.success("Activation pack updated");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update activation pack");
    },
  });
};

export const useSendPackLink = () => {
  return useMutation({
    mutationFn: ({
      packId,
      emails,
      kind,
      campaignId,
    }: {
      packId: string;
      emails: string[];
      kind: "presentation" | "app";
      campaignId: string;
    }) => ActivationPackService.sendPackLink(packId, emails, kind, campaignId),
  });
};

export const useDeleteActivationPack = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (packId: string) => ActivationPackService.deletePack(packId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["activation-packs"] });
      toast.success("Activation pack deleted");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete activation pack");
    },
  });
};
